/**
 * The Front Desk chat.
 *
 * Her words: "We use Google chat to communicate during the day. The chat room
 * is called Front Desk - it is about the day the clients and it's the main hub
 * between the nurses , the front desk and the provider (who works mostly
 * remote) can we create a chat in the software for this? The button to the
 * chat should be right off front desk and calendar view and alarm thru the
 * speaker when somone types in it".
 *
 * So: one button, on both of those pages, that opens a panel over the page
 * rather than navigating away — nobody should lose the calendar to answer
 * "is the 11 o'clock here yet". It polls every five seconds, chimes once for
 * somebody else's message, and never chimes for your own.
 *
 * Nothing here can reach a client: the API has no sender and no recipient.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, Send, Trash2, X, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui";
import { ping, setSoundsOn, soundsOn } from "@/lib/sounds";
import { isStaffColor } from "@/components/ui/Avatar";
import { useMe } from "@/lib/me";

const POLL_MS = 5000;

export interface TeamChannel {
  id: string;
  name: string;
  description: string | null;
  unread: number;
  last_at: string | null;
}

export interface TeamMessage {
  id: string;
  channel_id: string;
  author_id: string | null;
  author_name: string;
  /** Their assigned colour, so the same person is the same shade everywhere. */
  author_color?: string | null;
  body: string;
  contact_id: string | null;
  contact_name: string | null;
  created_at: string;
}

/* The chime now lives in lib/sounds.ts alongside the doorbell and the form
   tone, because they have to be audibly different from one another and that
   is easier to keep true when they are written side by side. */

/** Unread across every room, polled whether or not the panel is open. */
export function useTeamChatUnread(open: boolean) {
  const [unread, setUnread] = useState(0);
  const seen = useRef<string | null>(null);
  const soundOn = useRef(soundsOn());

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await api.get<{ channels: TeamChannel[] }>("/team-chat/channels");
        if (!alive) return;
        const total = d.channels.reduce((n, c) => n + c.unread, 0);
        const latest = d.channels.reduce<string | null>(
          (a, c) => (c.last_at && (!a || c.last_at > a) ? c.last_at : a), null);
        // Chime on a NEW message from somebody else, not on every poll that
        // still has unread in it, and never on the first load of the session.
        if (seen.current !== null && latest && latest > seen.current && total > 0 && !open && soundOn.current) {
          ping("chat");
        }
        seen.current = latest ?? seen.current ?? "";
        setUnread(total);
      } catch { /* the badge is not worth a toast */ }
    };
    void load();
    const t = setInterval(() => void load(), POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [open]);

  return unread;
}

/** The button that lives on the front desk and the calendar. */
/**
 * The Front Desk chat button.
 *
 * "can we add the front desk chat button to the messages page as well and can
 * we make it a color to pop and if it could be in the same location for each
 * page would be great."
 *
 * All three at once: it is mounted ONCE in the layout rather than dropped into
 * individual page headers, so it sits in the same corner of every screen
 * including Messages, and it is the one strong colour on the page. A thing you
 * reach for twenty times a day should be somewhere your hand already knows.
 */
/**
 * The way into the team chat.
 *
 * Two shapes, because one corner cannot serve both screens:
 *
 *   "toolbar" — a phone. It sits in the top bar beside the bell.
 *       "View from the phone the chat link is blocking send for email maybe
 *        just put all the chat tabs top right?" — she was right. On a phone
 *        the bottom-right corner is where Send lives, and on the calendar it
 *        is where the morning's appointments are.
 *
 *   "fab" — a desktop. The floating button in the same corner of every page,
 *       which is what she asked for originally and still likes.
 *
 * Only one is ever mounted (see useMediaQuery in CrmLayout / TopNav), so there
 * is a single unread poller however the window is sized.
 */
export function FrontDeskChatButton({
  variant = "fab", className,
}: { variant?: "fab" | "toolbar"; className?: string }) {
  const [open, setOpen] = useState(false);
  const unread = useTeamChatUnread(open);
  const toolbar = variant === "toolbar";
  return (
    <>
      <button
        type="button"
        data-chat-button
        data-chat-variant={variant}
        onClick={() => setOpen(true)}
        aria-label={unread > 0 ? `Front Desk chat, ${unread} unread` : "Front Desk chat"}
        title="Front Desk chat"
        className={cn(
          "inline-flex shrink-0 items-center justify-center text-white transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fuchsia-400",
          toolbar
            ? "relative h-8 w-8 rounded-lg border border-zinc-800 bg-fuchsia-600/90 hover:bg-fuchsia-500"
            : "fixed bottom-5 right-5 z-40 gap-2 rounded-full bg-fuchsia-600 px-4 py-3 text-sm font-semibold shadow-lg shadow-fuchsia-900/40 hover:bg-fuchsia-500",
          className,
        )}
      >
        <MessageCircle className={toolbar ? "h-4 w-4" : "h-5 w-5"} />
        <span className={toolbar ? "sr-only" : ""}>Front Desk chat</span>
        {unread > 0 && (
          <span
            data-chat-unread
            className={cn(
              "inline-flex items-center justify-center rounded-full bg-white font-bold text-fuchsia-700",
              toolbar
                ? "absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[10px] leading-none ring-2 ring-zinc-950"
                : "h-5 min-w-5 px-1.5 text-[11px]",
            )}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && <FrontDeskChatPanel onClose={() => setOpen(false)} />}
    </>
  );
}

export function FrontDeskChatPanel({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const [channels, setChannels] = useState<TeamChannel[] | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [soundOn, setSoundOn] = useState(soundsOn());
  const feedRef = useRef<HTMLDivElement | null>(null);
  const lastAt = useRef<string | null>(null);

  // Pinned to the newest message unless she has scrolled up to read back.
  const pinned = useRef(true);
  const scrollToEnd = () => {
    const el = feedRef.current;
    if (el && pinned.current) el.scrollTop = el.scrollHeight;
  };

  useEffect(() => {
    api.get<{ channels: TeamChannel[] }>("/team-chat/channels")
      .then((d) => {
        setChannels(d.channels);
        setChannelId((id) => id ?? d.channels[0]?.id ?? null);
      })
      .catch(() => setChannels([]));
  }, []);

  const markRead = useCallback((id: string) => {
    void api.post(`/team-chat/channels/${id}/read`, {}).catch(() => {});
  }, []);

  useEffect(() => {
    if (!channelId) return;
    let alive = true;
    lastAt.current = null;
    setMessages([]);
    const poll = async () => {
      try {
        const d = await api.get<{ messages: TeamMessage[] }>(
          `/team-chat/channels/${channelId}/messages`,
          lastAt.current ? { after: lastAt.current } : undefined,
        );
        if (!alive || d.messages.length === 0) return;
        lastAt.current = d.messages[d.messages.length - 1]!.created_at;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...d.messages.filter((m) => !seen.has(m.id))];
        });
        markRead(channelId);
        requestAnimationFrame(scrollToEnd);
      } catch { /* keep the panel up */ }
    };
    void poll();
    const t = setInterval(() => void poll(), POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [channelId, markRead]);

  const send = async () => {
    const body = draft.trim();
    if (!body || !channelId || sending) return;
    setSending(true);
    try {
      const d = await api.post<{ message: TeamMessage }>(`/team-chat/channels/${channelId}/messages`, { body });
      setDraft("");
      pinned.current = true;
      setMessages((prev) => (prev.some((m) => m.id === d.message.id) ? prev : [...prev, d.message]));
      lastAt.current = d.message.created_at;
      requestAnimationFrame(scrollToEnd);
    } catch { /* the draft stays in the box */ }
    finally { setSending(false); }
  };

  const remove = async (id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    await api.delete(`/team-chat/messages/${id}`).catch(() => {});
  };

  const channel = channels?.find((c) => c.id === channelId) ?? null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-label="Front Desk chat">
      <button type="button" aria-label="Close chat" className="flex-1 bg-black/40" onClick={onClose} />
      <div data-chat-panel className="flex h-full w-full max-w-md flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl">
        <header className="flex items-center gap-2 border-b border-zinc-800 px-4 py-3">
          <MessageCircle className="h-4 w-4 text-indigo-300" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-zinc-100">{channel?.name ?? "Front Desk"}</p>
            {channel?.description && <p className="truncate text-[11px] text-zinc-500">{channel.description}</p>}
          </div>
          <button
            type="button"
            title={soundOn ? "Turn the chime off" : "Turn the chime on"}
            onClick={() => {
              const next = !soundOn;
              setSoundOn(next);
              setSoundsOn(next);
              if (next) ping("chat");
            }}
            className="rounded p-1.5 text-zinc-500 hover:text-zinc-200"
          >
            {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1.5 text-zinc-500 hover:text-zinc-200">
            <X className="h-4 w-4" />
          </button>
        </header>

        {channels && channels.length > 1 && (
          <div className="flex gap-1 overflow-x-auto border-b border-zinc-800/70 px-2 py-1.5">
            {channels.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setChannelId(c.id)}
                className={cn(
                  "shrink-0 rounded-md px-2.5 py-1 text-xs",
                  c.id === channelId ? "bg-zinc-800 font-semibold text-zinc-100" : "text-zinc-500 hover:text-zinc-200",
                )}
              >
                {c.name}
                {c.unread > 0 && c.id !== channelId && (
                  <span className="ml-1.5 rounded-full bg-indigo-500 px-1.5 text-[10px] text-white">{c.unread}</span>
                )}
              </button>
            ))}
          </div>
        )}

        <div
          ref={feedRef}
          data-chat-feed
          onScroll={(e) => {
            const el = e.currentTarget;
            pinned.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          }}
          className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
        >
          {messages.length === 0 && (
            <p className="mt-8 text-center text-xs text-zinc-600">
              Nothing yet today. This room is for the team - clients never see it.
            </p>
          )}
          {messages.map((m) => {
            /* "can we have the person logged in to left and others to right
               and color?" — hers on the left, everybody else on the right,
               each in the colour assigned to them. Note this is the opposite
               way round to a phone's messages app, and it is deliberate:
               she asked for it this way. */
            const mine = !!me && m.author_id === me.id;
            const tint = isStaffColor(m.author_color) ? `bubble-${m.author_color}` : "";
            return (
              <div
                key={m.id}
                data-chat-message
                data-mine={mine ? "1" : "0"}
                className={cn("group flex flex-col", mine ? "items-start" : "items-end")}
              >
                <p className={cn("flex items-baseline gap-2 text-[11px]", !mine && "flex-row-reverse")}>
                  <span className="font-semibold text-zinc-300">{mine ? "You" : m.author_name}</span>
                  <span className="text-zinc-600">{formatRelative(m.created_at)}</span>
                  <button
                    type="button"
                    title="Remove"
                    onClick={() => void remove(m.id)}
                    className="rounded p-0.5 text-zinc-700 opacity-0 transition group-hover:opacity-100 hover:text-rose-400"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </p>
                <p
                  className={cn(
                    "mt-0.5 max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-lg border px-3 py-2 text-sm text-zinc-100",
                    tint || "border-zinc-800 bg-zinc-900/70 text-zinc-200",
                  )}
                >
                  {m.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="border-t border-zinc-800 p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
              }}
              rows={2}
              placeholder="Message the team"
              aria-label="Message the team"
              className="min-h-16 flex-1 resize-none rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
            />
            <Button onClick={() => void send()} disabled={!draft.trim() || sending} className="shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1.5 text-[10px] text-zinc-600">Enter sends, Shift+Enter for a new line. Staff only - no client ever sees this.</p>
        </div>
      </div>
    </div>
  );
}
